import {
	type useFireAdControlAnalyticsEvents,
	type useFireCrossFlowAnalyticsEvents,
} from '@atlassian/surface-analytics';
import { type SurfaceSdkConfig } from '@atlassian/surface-sdk';

import {
	type Actions,
	type AppRecommendationsInteractedActionPayload,
	type AppRecommendationsViewedScreenPayload,
	type CrossFlowInteractedActionPayload,
	type CrossFlowViewedUIActionPayload,
} from '../../common/types/spec';
// eslint-disable-next-line @atlassian/tangerine/import/no-restricted-paths
import { type AppRecommendationProps } from '../../ui/app-recommendations/app-recommendation/types';

type RecommendationFields = {
	apps?: AppRecommendationProps[];
};

export const useAnalyticsSideEffect =
	(
		adControlAnalyticsMethods: ReturnType<typeof useFireAdControlAnalyticsEvents>,
		surfaceAnalyticsMethods: ReturnType<typeof useFireCrossFlowAnalyticsEvents>,
	): NonNullable<SurfaceSdkConfig<any, Actions>['sideEffects']>[0] =>
	({ action, contexts }) => {
		const fields = contexts.module?.fields as RecommendationFields;

		const recommendationSession = {
			type: 'appRec' as const,
			id: contexts.client?.sessionId ?? '',
		};

		switch (action.type) {
			case 'USER_VIEW_DISCOVERY_SURFACE':
				surfaceAnalyticsMethods.fireViewedUIEvent(action.payload.action, {
					source: action.payload.source,
					recommendedProductIds: fields?.apps?.map((app) => app.productId) || [],
					recommendationSession,
				});

				break;
			case 'CLICK_APP_RECOMMENDATION':
				surfaceAnalyticsMethods.fireInteractedUIEvent(
					action.payload.action,
					{
						source: action.payload.source,
						targetProductId: action.payload.productId,
						recommendedProductIds: fields?.apps?.map((app) => app.productId),
						recommendationSession,
					},
					{
						skipBenefitsPageExperimentCohort: action.payload.skipBenefitsPageExperimentCohort,
						tblzSwitcherRoleBasedExperimentCohort:
							action.payload.tblzSwitcherRoleBasedExperimentCohort,
					},
				);

				break;
			case 'DISMISS_APP':
			case 'OPEN_APP_DROP_DOWN_MENU':
			case 'SHOW_REASONS':
			case 'CLOSE_APP_DISMISSED_FLAG':
			case 'CLICK_PRIVACY_POLICY_LINK':
			case 'DISMISS_RECOMMENDATION_REASONS_MODAL':
				adControlAnalyticsMethods.fireInteractedUIEvent(
					action.payload.action,
					{
						source: action.payload.source,
						targetProductId: action.payload.productId,
						recommendedProductIds: fields?.apps?.map((app) => app.productId),
						recommendationSession,
					},
					{
						tblzSwitcherRoleBasedExperimentCohort:
							action.payload.tblzSwitcherRoleBasedExperimentCohort,
					},
				);

				break;
			case 'USER_VIEW_RECOMMENDED_REASONS_SCREEN':
				adControlAnalyticsMethods?.fireViewedScreenEvent(action.payload.name, {
					recommendedProductIds: fields?.apps?.map((app) => app.productId) || [],
					recommendationSession: recommendationSession,
					targetProductId: action.payload.productId,
				});

				break;
			default:
				break;
		}
	};

export type {
	Actions,
	AppRecommendationsInteractedActionPayload,
	AppRecommendationsViewedScreenPayload,
	CrossFlowInteractedActionPayload,
	CrossFlowViewedUIActionPayload,
};
